import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Container, LinearProgress, Alert, AlertTitle, useTheme, useMediaQuery } from "@mui/material";
import { Work as WorkIcon, Business as BusinessIcon, Email as EmailIcon, Phone as PhoneIcon, LocationOn as LocationIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { downloadDataStoreSheet, readApiError } from "../UploadCsv";
import BulkEmailVerification from "./BulkEmailVerification";
import HeaderSection from "./HeaderSection";
import FiltersSection from "./FiltersSection";
import DataTable from "./DataTable";
import BatchManagement from "./BatchManagement";

const DataRows = () => {
  const { woocommerceId: id, recordId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [csvLoading, setCsvLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [allEmailsForVerification, setAllEmailsForVerification] = useState([]);


  const [filters, setFilters] = useState({
    event_name: "",
    company_name: "",
    email: "",
    phone_number: "",
    city: "",
    country: "",
    industry: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/v1/get-product-rows", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          ...filters,
          woocommerce_product_id: id,
        },
      });
      setData(response.data.data);
      setTotalRecords(response.data.pagination?.totalRecords || 0);
      setError("");

      // Fetch ALL unverified/null emails across all pages for Debounce verification
      const total = response.data.pagination?.totalRecords || 0;
      const totalPages = Math.ceil(total / 100);
      let allUnverified = [];
      for (let p = 1; p <= totalPages; p++) {
        const res = await axios.get("/api/v1/get-product-rows", {
          params: {
            page: p,
            limit: 100,
            woocommerce_product_id: id,
          },
        });
        const rows = res.data.data || [];
        const unverified = rows
          .filter((row) => row.varified === false || row.varified === null)
          .map((row) => ({ email: row.email, id: row.id }));
        allUnverified = [...allUnverified, ...unverified];
      }
      setAllEmailsForVerification(allUnverified);
      console.log(`[Debounce] Total unverified emails loaded: ${allUnverified.length}`);

    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [id, page, rowsPerPage, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilters({ ...filters, [event.target.name]: event.target.value });
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({
      event_name: "",
      company_name: "",
      email: "",
      phone_number: "",
      city: "",
      country: "",
      industry: "",
    });
    setPage(0);
  };

  const handleDownloadCSV = async () => {
    try {
      setCsvLoading(true);
      await downloadDataStoreSheet(id, data.find((row) => row.event_name)?.event_name);
      toast.success("Excel downloaded");
    } catch (err) {
      toast.error(await readApiError(err));
    } finally {
      setCsvLoading(false);
    }
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(`/api/v1/delete-record/${recordId}`);
      fetchData();
      toast.success("Record deleted successfully");
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter((value) => value.trim() !== "").length;
  };

  const getVerifiedCount = () => {
    return data.filter((row) => row.varified).length;
  };

  const getUnverifiedCount = () => {
    return data.filter((row) => !row.varified).length;
  };

  const filterFields = [
    { name: "event_name", label: "Event Name", icon: <WorkIcon /> },
    { name: "company_name", label: "Company Name", icon: <BusinessIcon /> },
    { name: "email", label: "Email", icon: <EmailIcon /> },
    { name: "phone_number", label: "Phone Number", icon: <PhoneIcon /> },
    { name: "city", label: "City", icon: <LocationIcon /> },
    { name: "country", label: "Country", icon: <LocationIcon /> },
    { name: "industry", label: "Industry", icon: <WorkIcon /> },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>

      <HeaderSection
        totalRecords={totalRecords}
        getVerifiedCount={getVerifiedCount}
        getUnverifiedCount={getUnverifiedCount}
        dataLength={data.length}
        fetchData={fetchData}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        getActiveFiltersCount={getActiveFiltersCount}
        handleResetFilters={handleResetFilters}
        isMobile={isMobile}
      />

      <FiltersSection
        showFilters={showFilters}
        filterFields={filterFields}
        filters={filters}
        handleFilterChange={handleFilterChange}
        getActiveFiltersCount={getActiveFiltersCount}
        handleResetFilters={handleResetFilters}
      />
      <BatchManagement isMobile={false} recordId={recordId} />

      <BulkEmailVerification
        emails={allEmailsForVerification}
        onVerificationComplete={fetchData}
        handleDownloadCSV={handleDownloadCSV}
        csvLoading={csvLoading}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}
      <DataTable
        data={data}
        loading={loading}
        rowsPerPage={rowsPerPage}
        page={page}
        totalRecords={totalRecords}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleDelete={handleDelete}
        fetchData={fetchData}
      />
    </Container>
  );
};

export default DataRows;
